import React from 'react';
import { styled as styled2 } from '@compiled/react';
import DOMPurify from 'dompurify';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { textAreaAttributeProps } from '@atlassian/jira-servicedesk-insight-shared-ui/src/common/constants/index.tsx';
import { ALLOWED_TEXT_AREA_ATTRIBUTE_HTML_TAGS } from '@atlassian/jira-servicedesk-insight-shared-utils/src/common/constants.tsx';
import messages from '../../common/messages.tsx';
import { ReadViewContainer } from '../../common/ui/styled.tsx';

type Props = {
	value: string | null;
	showPlaceholderIfEmpty?: boolean;
};

const sanitize = (html: string): string =>
	DOMPurify.sanitize(html, {
		// @ts-expect-error - TS2769 - No overload matches this call.
		ALLOWED_TAGS: ALLOWED_TEXT_AREA_ATTRIBUTE_HTML_TAGS,
		ALLOWED_ATTR: ['role', 'tabindex', 'href', 'target'],
	});

export const TextAreaReadView = ({
	value,
	showPlaceholderIfEmpty = true,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const nonNullValue = value ?? '';
	const hasValue = nonNullValue.length > 0;
	const placeholderText = showPlaceholderIfEmpty
		? formatMessage(messages.placeholderNoneEntered)
		: null;
	return (
		<ReadViewContainer isPlaceholder={!hasValue}>
			{hasValue ? (
				<ReadOnlyView dangerouslySetInnerHTML={{ __html: sanitize(nonNullValue) }} />
			) : (
				placeholderText
			)}
		</ReadViewContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadOnlyView = styled2.div({
	marginBottom: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: textAreaAttributeProps.maxHeight,
	overflowY: 'auto',
	overflowWrap: 'break-word',
	wordBreak: 'break-word',
	whiteSpace: 'pre-wrap',
});
